import { IonButton, useIonModal, IonPage } from '@ionic/react';
import classNames from 'classnames';
import {
	AdminActionFilterParamType,
	AdminActionReason,
	AdminActionsTaken,
	EmptyNewAdminActionFilter,
	FullAdminAction,
	addDays,
	dateDiffDays,
	getNow
} from 'model';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { useResolveAdminActionMutation } from '../../api/AdminActionApi';
import { useSetDeletedCommentMutation } from '../../api/CommentApi';
import { useSetDeletedPostMutation } from '../../api/PostApi';
import { useSetActiveMutation, useSuspendUserMutation } from '../../api/UserApi';
import { Header } from '../../components/Header';
import { IconButton } from '../../components/IconButton';
import { IconArrowBack, IconFilter, IconReport } from '../../components/QuirkIcons';
import { QuirkIonContent } from '../../components/QuirkIonContent';
import { QuirkIonPage } from '../../components/QuirkIonPage';
import { QuirkLoader } from '../../components/QuirkLoader';
import { AdminReportItem } from '../../components/admin/AdminReporItem';
import { useReportFilters } from '../../hooks/useReportFilters';
import { useUserHasRole } from '../../hooks/useUserHasRole';
import { getDaysBackRange } from '../../util/getDaysBackRange';
import { AdminOnly } from './AdminOnly';
import { ReportAction } from './ReportAction';
import { ReportFilters } from './ReportFilters';
import styles from './ReportList.module.css';
import { Badge } from '../../components/Badge.js';
import { NoResults } from '../../components/NoResults.js';

export interface ActionFormValuesType {
	documentId: string;
	documentType: string;
	description: string;
	sortBy: string;
	reason: AdminActionReason;
	actionTaken: AdminActionsTaken;
}
export const ReportList: React.FC<{
	user?: string;
	onDismiss?: () => {};
	modal?: boolean;
}> = ({ onDismiss, user, modal }) => {
	const EmptyNewReportFilterWithDate = {
		...EmptyNewAdminActionFilter,
		dateRange: getDaysBackRange(user ? '2000' : '7'),
		userid: user && user
	};

	const [selectedItem, setSelectedItem] = useState<FullAdminAction>();
	const [actionFormValues, setActionFormValues] = useState<ActionFormValuesType>({
		documentId: '',
		sortBy: 'flags',
		documentType: 'all',
		description: '',
		reason: 'offensive',
		actionTaken: 'created'
	});
	const admin = useUserHasRole('admin');
	const history = useHistory();
	const [resolveAdminAction] = useResolveAdminActionMutation();
	const [setDeletedComment] = useSetDeletedCommentMutation();
	const [setDeletedPost] = useSetDeletedPostMutation();
	const [suspendUser] = useSuspendUserMutation();
	const [setActive] = useSetActiveMutation();

	const [reportFilterValues, setReportFilterValues] =
		useState<AdminActionFilterParamType>(EmptyNewReportFilterWithDate);
	const { FullAdminActionFeed, ActionableActionsFeed, isLoading } = useReportFilters(reportFilterValues);
	const diffDays = Math.floor(dateDiffDays(reportFilterValues.dateRange[0], reportFilterValues.dateRange[1]));
	const setValues = (val: AdminActionFilterParamType) => {
		const updatedValues: AdminActionFilterParamType = reportFilterValues;
		setReportFilterValues({ ...updatedValues, ...val });
	};
	// eslint-disable-next-line no-undef
	const setActionValues = (e: React.FormEvent<HTMLIonRadioGroupElement>, id: string, type: string) => {
		setActionFormValues({
			...actionFormValues,
			documentId: id,
			documentType: type,
			[e.currentTarget.name]: e.currentTarget.value
		});
	};

	const openModal = (item: FullAdminAction) => {
		setSelectedItem(item);
		presentReportAction();
	};
	const deletePostOrComment = () => {
		if (actionFormValues.documentType === 'post') {
			setDeletedPost({ id: actionFormValues.documentId, deleted: true });
		} else if (actionFormValues.documentType === 'comment') {
			setDeletedComment({ id: actionFormValues.documentId, deleted: true });
		}
	};
	const resolveReports = (authorId: string, adminReportId: string) => {
		resolveAdminAction({
			adminActionId: adminReportId,
			actionTaken: actionFormValues.actionTaken,
			reason: actionFormValues.reason,
			description: actionFormValues.description
		});

		if (actionFormValues.actionTaken === 'blockedUser' || actionFormValues.actionTaken === 'deleted') {
			deletePostOrComment();
		}
		if (actionFormValues.actionTaken === 'blockedUser') {
			setActive({ userid: authorId, active: false });
		}
		if (actionFormValues.actionTaken === 'pausedUser') {
			suspendUser({ userid: authorId, suspendedUntil: addDays(getNow(), 7) });
			deletePostOrComment();
		}
	};

	// Report filter page
	const [presentReportFilter, dismissReportFilter] = useIonModal(ReportFilters, {
		reportFilterValues,
		setValues,
		onDismiss: () => dismissReportFilter()
	});

	// Report action page
	const [presentReportAction, dismissReportAction] = useIonModal(ReportAction, {
		selectedItem,
		setActionValues,
		actionFormValues,
		onDismiss: () => dismissReportAction(),
		resolveReports
	});

	const customTimeMessage = () => {
		if (diffDays < 32) {
			return `No results found within the last ${diffDays > 1 ? diffDays : ''} day${diffDays > 1 ? 's' : ''}.`;
		} else if (diffDays === 365) {
			return 'No results found within the past year.';
		} else {
			return 'No results found for over a year.';
		}
	};
	const PageComponent = modal ? IonPage : QuirkIonPage;
	return (
		<PageComponent name="Reported Users">
			<>
				<Header
					title="Reported users"
					left={
						<IconButton
							icon={<IconArrowBack />}
							onClick={() => {
								onDismiss ? onDismiss() : history.go(-1);
							}}
						/>
					}
					right={
						admin && ActionableActionsFeed ? (
							<Badge
								content={ActionableActionsFeed.length}
								type={ActionableActionsFeed.length > 0 ? 'alert' : 'info'}
							/>
						) : undefined
					}
				/>

				<QuirkIonContent flush fullscreen>
					<AdminOnly>
						<>
							<div className={styles.filterButtonContainer}>
								<div className={classNames(styles.filterButtonContainerInner)}>
									<IonButton
										className={styles.filterButton}
										fill="clear"
										size="small"
										onClick={() => presentReportFilter()}
									>
										<IconFilter />
										{<div className={styles.filterButtonLabel}>Filter</div>}
									</IonButton>
								</div>
							</div>
							<QuirkLoader
								isLoading={isLoading}
								loadingMessage="Loading reports..."
								data={FullAdminActionFeed}
								noResults={
									<NoResults
										testId="reportsNoResults"
										icon={<IconReport />}
										subheading="Refine your filters."
										heading={customTimeMessage()}
									/>
								}
							>
								<Virtuoso
									totalCount={FullAdminActionFeed.length}
									data={FullAdminActionFeed}
									itemContent={(index, item) => (
										<div>{<AdminReportItem item={item} openModal={() => openModal(item)} />}</div>
									)}
								/>
							</QuirkLoader>
						</>
					</AdminOnly>
				</QuirkIonContent>
			</>
		</PageComponent>
	);
};
